import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import HomeIcon from '@mui/icons-material/Home';
import BarChartIcon from '@mui/icons-material/BarChart';
import AnalyticsIcon from '@mui/icons-material/Analytics';

const RapportModulesAgricoles = () => {
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const volumes = [
    {
      id: 1,
      title: "VOLUME I : TABLEAUX STATISTIQUES DU MODULE COMMUNAUTAIRE",
      subtitle: "RGPHAE 2024",
      type: "tableaux",
      summary: "Recueil complet des tableaux statistiques du module communautaire avec indicateurs détaillés",
      module: "communautaire",
      year: "2024",
      pdfUrl: "/documents/RGPHAE/VOLUME-I-TABLEAUX-MODULE-COMMUNAUTAIRE-RGAE-2024-2027-BURUNDI-Version-Finale-16Septembre2025.pdf",
    },
    {
      id: 2,
      title: "VOLUME II : ANALYSE DES RÉSULTATS DU MODULE COMMUNAUTAIRE",
      subtitle: "RGPHAE 2024",
      type: "analyse",
      summary: "Analyse approfondie des infrastructures et services au niveau communautaire",
      module: "communautaire",
      year: "2024",
      pdfUrl: "/documents/RGPHAE/VOLUME-II-RAPPORT-ANALYSE-MODULE-COMMUNAUTAIRE-RGPHAE-BURUNDI-2024-Version-Finale-16Septembre2025.pdf",
    },
    {
      id: 3,
      title: "VOLUME III : TABLEAUX STATISTIQUES DU MODULE AGRICOLE DE BASE",
      subtitle: "RGPHAE 2024",
      type: "tableaux",
      summary: "Données exhaustives sur les ménages agricoles et les pratiques culturales",
      module: "agricole-base",
      year: "2024",
      pdfUrl: "/documents/RGPHAE/VOLUME-III-TABLEAUX-MODULE-AGRICOLE-BASE-RGPHAE-BURUNDI-2024-Version-Final-16Septembre2025.pdf",
    },
    {
      id: 4,
      title: "VOLUME IV : ANALYSE DU MODULE AGRICOLE DE BASE",
      subtitle: "RGPHAE 2024",
      type: "analyse",
      summary: "Analyse stratégique du secteur agricole traditionnel au Burundi",
      module: "agricole-base",
      year: "2024",
     pdfUrl: "/documents/RGPHAE/VOLUME-IV-RAPPORT-ANALYSE-MODULE-AGRICOLE-BASE-RGPHAE-BURUNDI-2024-Version-Finale-16Septembre2025.pdf",
    },
    {
      id: 5,
      title: "VOLUME V : TABLEAUX DES EXPLOITATIONS AGROPASTORALES MODERNES",
      subtitle: "RGPHAE 2025",
      type: "tableaux",
      summary: "Données quantitatives sur les exploitations agricoles modernes",
      module: "exploitations-modernes",
      year: "2025",
      pdfUrl: "/documents/RGPHAE/VOLUME-V-TABLEAUX-MODULE-EM-RGAE-BURUNDI-2025-Version-Finale-16Septembre2025.pdf",
    },
    {
      id: 6,
      title: "VOLUME VI : ANALYSE DES EXPLOITATIONS AGROPASTORALES MODERNES",
      subtitle: "RGPHAE 2025",
      type: "analyse",
      summary: "Étude des modèles d'exploitations agricoles modernes et leur rentabilité",
      module: "exploitations-modernes",
      year: "2025",
      pdfUrl: "/documents/RGPHAE/VOLUME-VI-RAPPORT-ANALYSE-MODULE-EM-RGAE-BURUNDI-2025-Version-Finale-16Septembre2025.pdf",
    },
    {
      id: 7,
      title: "VOLUME VII : TABLEAUX DES ORGANISATIONS DE PRODUCTEURS AGRICOLES",
      subtitle: "RGPHAE 2025",
      type: "tableaux",
      summary: "Statistiques détaillées sur les coopératives et associations agricoles",
      module: "opa",
      year: "2025",
      pdfUrl: "/documents/RGPHAE/VOLUME-VII-TABLEAUX-MODULE-OPA-RGAE-BURUNDI-2025-Version-Finale-16Septembre2025.pdf",
    },
    {
      id: 8,
      title: "VOLUME VIII : ANALYSE DES ORGANISATIONS DE PRODUCTEURS AGRICOLES",
      subtitle: "RGPHAE 2025",
      type: "analyse",
      summary: "Analyse du rôle des OPA dans le développement agricole",
      module: "opa",
      year: "2025",
      pdfUrl: "/documents/RGPHAE/VOLUME-VIII-RAPPORT-ANALYSE-MODULE-OPA-RGAE-BURUNDI-2025-Version-Finale-16Septembre2025.pdf",
    }
  ];

  const [filter, setFilter] = useState('all');

  const filteredVolumes = filter === 'all' 
    ? volumes 
    : volumes.filter(volume => volume.type === filter);

  const getModuleColor = (module) => {
    const colors = {
      'communautaire': '#008D36', 
      'agricole-base': '#008D36', 
      'exploitations-modernes':'#008D36', 
      'opa': '#008D36' 
    };
    return colors[module] || '#757575';
  };

  const getTypeColor = (type) => {
    return type === 'tableaux' ? '#E30613' : '#E30613';
  };

  const getTypeIcon = (type) => {
    return type === 'tableaux' ? <BarChartIcon /> : <AnalyticsIcon />;
  };

  const handleDownload = async (volume) => {
    setDownloadLoading(volume.id);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const link = document.createElement('a');
      link.href = volume.pdfUrl;
      link.download = volume.pdfUrl.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSnackbar({
        open: true,
        message: `Téléchargement du ${volume.title} commencé`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors du téléchargement',
        severity: 'error'
      });
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getModuleDisplayName = (module) => {
    const names = {
      'communautaire': 'Communautaire',
      'agricole-base': 'Agricole de Base',
      'exploitations-modernes': 'Exploitations Modernes',
      'opa': 'Organisations de Producteurs'
    };
    return names[module] || module;
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <NavigationBar />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Fil d'Ariane */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link underline="hover" color="inherit" href="/" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Accueil
          </Link>
            <Link underline="hover" color="inherit" href="/resultatRGPHAE2024" sx={{ display: 'flex', alignItems: 'center' }}>
              Résultats RGPHAE 2024
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <BarChartIcon sx={{ mr: 0.5 }} fontSize="small" />
            Rapports Agricoles
          </Typography>
        </Breadcrumbs>

        {/* En-tête */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: '700', 
              color: '#008D36',
              fontSize: { xs: '1.75rem', md: '2.125rem' }
            }}
          >
            Rapports des Modules Agricoles RGPHAE
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary', 
              maxWidth: '800px', 
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: '400'
            }}
          >
            Collection complète des analyses statistiques et tableaux détaillés 
            des différents modules agricoles du recensement
          </Typography>
        </Box>

        {/* Filtres */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 1 }}>
          {[
            { key: 'all', label: 'Tous les volumes' },
            { key: 'tableaux', label: 'Tableaux statistiques' },
            { key: 'analyse', label: 'Rapports d\'analyse' }
          ].map((item) => (
            <Chip
              key={item.key}
              label={item.label}
              onClick={() => setFilter(item.key)}
              variant={filter === item.key ? "filled" : "outlined"}
              sx={{ 
                fontWeight: '600',
                minWidth: 100,
                backgroundColor: filter === item.key ? '#008D36' : 'transparent',
                color: filter === item.key ? 'white' : '#008D36',
                borderColor: '#008D36',
                '&:hover': {
                  backgroundColor: filter === item.key ? '#008D36' : 'rgba(0, 141, 54, 0.04)'
                }
              }}
            />
          ))}
        </Box>

        {/* Grille des publications */}
        <Grid container spacing={3}>
          {filteredVolumes.map((volume) => (
            <Grid item xs={12} md={6} key={volume.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease-in-out',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'white',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    borderColor: getModuleColor(volume.module),
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* En-tête de la carte */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Chip
                      icon={getTypeIcon(volume.type)}
                      label={volume.type === 'tableaux' ? 'Tableaux' : 'Analyse'}
                      size="small"
                      sx={{
                        backgroundColor: getTypeColor(volume.type),
                        color: 'white',
                        fontWeight: '600'
                      }}
                    />
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {volume.year}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Module */}
                  <Chip
                    label={getModuleDisplayName(volume.module)}
                    size="small"
                    sx={{
                      backgroundColor: getModuleColor(volume.module),
                      color: 'white',
                      mb: 2,
                      fontWeight: '500'
                    }}
                  />

                  {/* Titre */}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: '600',
                      lineHeight: 1.3,
                      mb: 1,
                      flexGrow: 1,
                      color: '#1a1a1a'
                    }}
                  >
                    {volume.title}
                  </Typography>

                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 2, 
                      fontWeight: '500',
                      color: '#008D36'
                    }}
                  >
                    {volume.subtitle}
                  </Typography>

                  {/* Résumé */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 2, lineHeight: 1.5 }}
                  >
                    {volume.summary}
                  </Typography>

                  {/* Bouton de téléchargement */}
                  <Box sx={{ mt: 'auto' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={
                        downloadLoading === volume.id ? 
                        <CircularProgress size={16} /> : 
                        <DownloadIcon />
                      }
                      disabled={downloadLoading === volume.id}
                      onClick={() => handleDownload(volume)}
                      sx={{
                        py: 1.5,
                        backgroundColor: '#008D36',
                        '&:hover': {
                          backgroundColor: '#008D36',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(227, 6, 19, 0.3)'
                        },
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      {downloadLoading === volume.id ? 'Téléchargement...' : 'Télécharger le PDF'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Message si aucun résultat */}
        {filteredVolumes.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucune publication trouvée
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Essayez de modifier les filtres de recherche
            </Typography>
          </Box>
        )}

        {/* Section informative */}
        <Box sx={{ 
          mt: 8, 
          p: 4, 
          backgroundColor: 'white', 
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#008D36',
              mb: 2,
              fontWeight: '600'
            }}
          >
            Données Officielles du RGPHAE
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              maxWidth: 600, 
              mx: 'auto', 
              lineHeight: 1.6 
            }}
          >
            Ces publications constituent la référence statistique officielle pour le secteur agricole burundais, 
            fournissant des données essentielles pour la recherche, la planification et l'élaboration des politiques publiques.
          </Typography>
        </Box>
      </Container>

      <Footer />

      {/* Notification */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            backgroundColor: snackbar.severity === 'success' ? '#008D36' : '#E30613',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RapportModulesAgricoles;